import React, { forwardRef, useMemo, useEffect, useState } from 'react';
import { Button } from '@amzn/awsui-components-console';
import { ApplicationProfileMetadata } from '@amzn/snow-data-service-typescript-client';

import {
	COLLECTION_PREFERENCES,
	DEFAULT_PREFERENCES,
} from './ApplicationListConstants';
import {
	GenericTable,
	GenericTableColumn,
	GenericComponentData,
} from 'src/components/commonComponents/GenericTable';


type ApplicationProfile = ApplicationProfileMetadata & GenericComponentData;
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import DeleteConfirmationModal from 'src/components/commonComponents/DeleteConfirmationModal/DeleteConfirmationModal';

interface ApplicationListProps {}

const ApplicationList = forwardRef<
	{ fetchData: () => void },
	ApplicationListProps
		>((_props, ref) => {
			const { navigate } = useNavigation();
			const {
				setSelectedApplication,
				selectedApplication,
				addNavigationError,
			} = useStore();

			const [deleteModalVisible, setDeleteModalVisible] = useState(false);
			const [applicationToDelete, setApplicationToDelete] = useState<string | null>(null);

			const { isLoading, data, error, fetchData } = useFetchForSDS({
				url: SDS_APIGATEWAY_ENDPOINT,
				path: '/application',
				queryParams: { count: 300 },
				method: 'GET',
			});

			const {
				result: {
					isLoading: isDeleting,
					error: deleteError,
				},
				onClickCB: deleteApplication,
			} = useFetchOnUserActionUsingSDS<void>();

			useEffect(() => {
				if (error) {
					addNavigationError({
						message: `Failed to load applications: ${error}`,
						type: 'error',
						timestamp: Date.now(),
					});
				}

				if (deleteError) {
					addNavigationError({
						message: `Failed to delete application: ${deleteError}`,
						type: 'error',
						timestamp: Date.now(),
					});
				}
			}, [error, deleteError, addNavigationError]);

			const items = useMemo(() => {
				if (!data) return [];

				return (
					(data as { applicationProfiles?: ApplicationProfile[] })
						?.applicationProfiles || []
				);
			}, [data]);

			const handleViewDetails = (applicationId: string) => {
				const application = items.find(
					(item) => item.applicationId === applicationId
				);

				if (application) {
					setSelectedApplication(application as ApplicationProfileMetadata);
					navigate(
						ROUTES.APPLICATION_DETAILS,
						`/applicationManagement/${applicationId}`
					);
				}
			};

			const handleAddApplication = () => {
				navigate(ROUTES.ADD_APPLICATION, '/applicationManagement/add');
			};

			const handleEditApplication = (applicationId: string) => {
				const application = items.find(
					(item) => item.applicationId === applicationId
				);

				if (application) {
					setSelectedApplication(application as ApplicationProfileMetadata);
					navigate(
						ROUTES.EDIT_APPLICATION,
						`/applicationManagement/${applicationId}/edit`
					);
				}
			};

			const handleDeleteApplication = (applicationId: string) => {
				setApplicationToDelete(applicationId);
				setDeleteModalVisible(true);
			};

			const confirmDelete = async () => {
				if (!applicationToDelete) return;

				await deleteApplication({
					path: `/application/${applicationToDelete}`,
					method: 'DELETE',
					url: SDS_APIGATEWAY_ENDPOINT,
				});

				if (selectedApplication?.applicationId === applicationToDelete) {
					setSelectedApplication(null);
				}

				setDeleteModalVisible(false);
				setApplicationToDelete(null);
				fetchData();
			};

			const cancelDelete = () => {
				setDeleteModalVisible(false);
				setApplicationToDelete(null);
			};

			const columns: GenericTableColumn<ApplicationProfile>[] = [
				{
					id: 'applicationName',
					header: 'Application Name',
					cell: (item: ApplicationProfile) => (
						<Button
							onClick={() => handleViewDetails(item.applicationId!)}
							variant="inline-link"
						>
							{item.applicationName}
						</Button>
					),
					sortingField: 'applicationName',
					minWidth: 150,
				},
				{
					id: 'description',
					header: 'Description',
					cell: (item: ApplicationProfile) => item.description,
				},
				{
					id: 'domain',
					header: 'Domain',
					cell: (item: ApplicationProfile) => item.domain,
					sortingField: 'domain',
				},
				{
					id: 'status',
					header: 'Status',
					cell: (item: ApplicationProfile) => (
						<ShowSnowStatus status={item.status} />
					),
					sortingField: 'status',
				},
			];

			const applicationName = items.find(
				(item) => item.applicationId === applicationToDelete
			)?.applicationName;

			return (
				<>
					<GenericTable<ApplicationProfile>
						ref={ref}
						title="Applications"
						items={items as ApplicationProfile[]}
						columns={columns}
						isLoading={isLoading}
						error={error}
						selectedItemId={selectedApplication?.applicationId || null}
						onSelectionChange={(id) => {
							const app = items.find((item) => item.applicationId === id);

							if (app) {
								setSelectedApplication(app as ApplicationProfileMetadata);
							} else {
								setSelectedApplication(null);
							}
						}}
						onRefresh={fetchData}
						primaryAction={{
							text: 'Add Application',
							onClick: handleAddApplication,
						}}
						dropdownActions={[
							{
								text: 'View Details',
								id: 'view-details',
								handler: handleViewDetails,
							},
							{
								text: 'Edit Application',
								id: 'edit-application',
								handler: handleEditApplication,
							},
							{
								text: 'Delete Application',
								id: 'delete-application',
								handler: handleDeleteApplication,
							},
						]}
						filterPlaceholder="Find Applications"
						filterFields={['applicationName', 'description', 'domain']}
						emptyTitle="No Applications found"
						emptyDescription="No applications found. Create a new Application to get started."
						emptyAction={{
							text: 'Add Application',
							onClick: handleAddApplication,
							variant: 'primary',
						}}
						collectionPreferences={COLLECTION_PREFERENCES}
						defaultPreferences={DEFAULT_PREFERENCES}
						testIdPrefix="application-list"
					/>
					<DeleteConfirmationModal
						visible={deleteModalVisible}
						onDismiss={cancelDelete}
						onConfirm={confirmDelete}
						isDeleting={isDeleting}
						itemName={applicationName || ''}
						itemType="application"
						testIdPrefix="delete-application"
					/>
				</>
			);
		});

ApplicationList.displayName = 'ApplicationList';

export default ApplicationList;
